import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { PageContainer, PageHeader, EmptyState } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";

export default function Managers() {
  const [users, setUsers] = useState([]);
  const [estates, setEstates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "estate_manager", estate_id: "", phone: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, sa, e] = await Promise.all([
        api.get("/users", { params: { role: "estate_manager" } }),
        api.get("/users", { params: { role: "super_admin" } }),
        api.get("/estates"),
      ]);
      setUsers([...sa.data, ...m.data]);
      setEstates(e.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const patch = { name: form.name, phone: form.phone };
        if (form.password) patch.password = form.password;
        if (form.role === "estate_manager") patch.estate_id = form.estate_id || null;
        await api.patch(`/users/${editing.id}`, patch);
        toast.success("User updated");
      } else {
        const body = { ...form };
        if (!body.estate_id) delete body.estate_id;
        await api.post("/users", body);
        toast.success(form.role === "super_admin" ? "Super admin created" : "Manager created");
      }
      setOpen(false); setEditing(null);
      setForm({ name: "", email: "", password: "", role: "estate_manager", estate_id: "", phone: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <AppShell>
      <PageContainer>
        <PageHeader
          eyebrow="Super Admin"
          title="Managers & Admins"
          description="Create additional super admins or estate managers. Assign each manager to an estate."
          actions={
            <Button
              onClick={() => { setEditing(null); setForm({ name: "", email: "", password: "", role: "estate_manager", estate_id: "", phone: "" }); setOpen(true); }}
              data-testid="create-user-button"
              className="rounded-none bg-black hover:bg-zinc-800 h-10 px-4 uppercase tracking-wider text-xs font-semibold"
            >
              <Plus className="h-4 w-4" /> New User
            </Button>
          }
        />

        {loading ? (
          <div className="label-eyebrow">Loading…</div>
        ) : users.length === 0 ? (
          <EmptyState title="No users yet" description="Create your first estate manager or a second super admin." />
        ) : (
          <div className="bg-white border border-zinc-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="border-b border-zinc-200 bg-zinc-50">
                <tr className="text-left">
                  <th className="p-4 label-eyebrow font-semibold">Name</th>
                  <th className="p-4 label-eyebrow font-semibold">Email</th>
                  <th className="p-4 label-eyebrow font-semibold">Role</th>
                  <th className="p-4 label-eyebrow font-semibold">Estate</th>
                  <th className="p-4 label-eyebrow font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const estate = estates.find((e) => e.id === u.estate_id);
                  return (
                    <tr key={u.id} className="border-b border-zinc-100" data-testid={`user-row-${u.id}`}>
                      <td className="p-4 font-semibold">{u.name}</td>
                      <td className="p-4 text-zinc-600">{u.email}</td>
                      <td className="p-4">
                        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${u.role === "super_admin" ? "bg-black text-white border-black" : "bg-zinc-100 text-zinc-800 border-zinc-300"}`}>
                          {u.role === "super_admin" ? "Super Admin" : "Manager"}
                        </span>
                      </td>
                      <td className="p-4 text-zinc-600">{estate?.name || "—"}</td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => { setEditing(u); setForm({ name: u.name, email: u.email, password: "", role: u.role, estate_id: u.estate_id || "", phone: u.phone || "" }); setOpen(true); }}
                            data-testid={`edit-user-${u.id}`}
                            className="p-2 border border-zinc-200 hover:bg-black hover:text-white hover:border-black transition-colors"
                          ><Pencil className="h-3.5 w-3.5" /></button>
                          <button
                            onClick={() => onDelete(u.id)}
                            data-testid={`delete-user-${u.id}`}
                            className="p-2 border border-zinc-200 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
                          ><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogContent className="rounded-none border-zinc-300" data-testid="user-form-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-black tracking-tight">
                {editing ? "Edit User" : "New User"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="label-eyebrow">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} disabled={!!editing}>
                  <SelectTrigger className="rounded-none border-zinc-300 mt-2" data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="estate_manager">Estate Manager</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="label-eyebrow">Name</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black mt-2" />
                </div>
                <div>
                  <Label className="label-eyebrow">Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="user-phone-input" className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black mt-2" />
                </div>
              </div>
              <div>
                <Label className="label-eyebrow">Email</Label>
                <Input required type="email" disabled={!!editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black mt-2" />
              </div>
              <div>
                <Label className="label-eyebrow">{editing ? "New Password (optional)" : "Password"}</Label>
                <Input required={!editing} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black mt-2" />
              </div>
              {form.role === "estate_manager" && (
                <div>
                  <Label className="label-eyebrow">Assign Estate</Label>
                  <Select value={form.estate_id || "none"} onValueChange={(v) => setForm({ ...form, estate_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="rounded-none border-zinc-300 mt-2" data-testid="user-estate-select">
                      <SelectValue placeholder="Select estate" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {estates.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <DialogFooter>
                <Button type="submit" data-testid="submit-user-button" className="rounded-none bg-black hover:bg-zinc-800 uppercase tracking-wider text-xs font-semibold">
                  {editing ? "Save Changes" : "Create User"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </PageContainer>
    </AppShell>
  );
}
